import React, { useState } from 'react';
import { Phone, Calculator, Star, Shield, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';
import QuoteModal from './QuoteModal';
import { useSettings } from '../hooks/useSettings';

const Hero = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { settings } = useSettings();

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(companyInfo.whatsappMessage);
    window.open(`https://wa.me/${settings.whatsappPhone}?text=${message}`, '_blank');
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image with Parallax */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: 'url(/hero-bg.jpg)',
        }}
      >
        {/* Dark Overlay with Gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-transparent"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="max-w-4xl mx-auto sm:mx-0 text-left">
          {/* Main Content Card - Subtle Glassmorphism */}
          <div className="bg-white/40 backdrop-blur-sm border border-white/50 p-5 sm:p-10 rounded-3xl shadow-2xl shadow-black/5 space-y-6 sm:space-y-8 animate-fade-in">
            {/* Trust Badges */}
            <div className="flex flex-wrap justify-start gap-3 text-xs sm:text-sm animate-fade-in-up delay-100">
              <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-100 uppercase tracking-wider font-semibold text-gray-600">
                <Clock className="w-3.5 h-3.5 text-[#EAB308]" />
                <span>20+ Anos</span>
              </div>
              <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-100 uppercase tracking-wider font-semibold text-gray-600">
                <Star className="w-3.5 h-3.5 text-[#EAB308] fill-[#EAB308]" />
                <span>4.9 Avaliação</span>
              </div>
              <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-100 uppercase tracking-wider font-semibold text-gray-600">
                <Shield className="w-3.5 h-3.5 text-[#EAB308]" />
                <span>Profissional Certificado</span>
              </div>
            </div>

            {/* Main Headline */}
            <div className="space-y-4 animate-fade-in-up delay-200">
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black leading-tight text-[#111827]">
                A Sua Mudança <span className="text-[#EAB308]">Começa Aqui</span>
                <span className="block text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-700 mt-2 opacity-90">
                  Rápida, Segura e Sem Complicações
                </span>
              </h1>
              <p className="text-base sm:text-xl text-gray-700 leading-relaxed max-w-2xl font-medium">
                Na Ponto a Ponto Transportes, garantimos uma mudança organizada, sem atrasos e sem danos — do primeiro contacto à entrega final.
              </p>
            </div>

            {/* Checklist items - Premium Look */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-fade-in-up delay-400">
              {[
                "Mudanças locais e regionais",
                "Profissionais experientes",
                "Compromisso com prazo e cuidado"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 group">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#EAB308]/10 flex items-center justify-center group-hover:bg-[#EAB308] transition-colors duration-300">
                    <CheckCircle2 className="w-5 h-5 text-[#EAB308] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <span className="text-gray-800 font-semibold">{item}</span>
                </div>
              ))}
            </div>

            {/* Social Proof Intro */}
            <p className="text-base sm:text-lg text-gray-600 font-medium max-w-2xl animate-fade-in-up delay-500">
              Peça agora o seu orçamento rápido e descubra porque somos uma das empresas de mudanças mais bem avaliadas de Lisboa.
            </p>

            {/* CTA Button */}
            <div className="flex justify-start animate-fade-in-up delay-600 w-full">
              <Button
                onClick={() => setIsModalOpen(true)}
                size="lg"
                className="w-full sm:w-auto bg-[#EAB308] hover:bg-[#CA8A04] text-black font-extrabold text-sm sm:text-lg px-4 sm:px-12 py-4 sm:py-8 h-auto rounded-2xl transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#EAB308]/40 group relative overflow-hidden whitespace-normal"
              >
                <span className="relative z-10 flex items-center justify-center text-center">
                  <Calculator className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 flex-shrink-0 group-hover:rotate-12 transition-all duration-300" />
                  <span>Receber Orçamento em até 1 hora no Whatsapp</span>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </Button>
            </div>

            {/* Social Proof Text */}
            <div className="flex items-center gap-2 text-gray-500 font-bold text-sm animate-fade-in delay-700">
              <div className="w-8 h-[2px] bg-[#EAB308]"></div>
              <span>+5.000 Mudanças bem-sucedidas</span>
            </div>
          </div>

          {/* YouTube Video Section */}
          <div className="mt-16 animate-fade-in-up delay-700">
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl hover:shadow-[#EAB308]/30 transition-all duration-500 hover:scale-105 group">
              {/* Glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-[#EAB308] via-[#CA8A04] to-[#EAB308] rounded-2xl blur opacity-30 group-hover:opacity-60 transition-opacity duration-500"></div>

              {/* YouTube iframe */}
              <iframe
                className="relative z-10 w-full h-full rounded-2xl"
                src={settings.videoUrl || "https://www.youtube.com/embed/VL1Nj7oHxbU"}
                title="Ponto a Ponto Transportes - Vídeo Apresentação"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-[#111827]/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-[#111827]/50 rounded-full animate-pulse"></div>
        </div>
      </div>

      {/* Quote Modal */}
      <QuoteModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
};

export default Hero;
